import { Button, Flex } from '@weareredlight/design-system'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { ChangeLogParamsType } from 'types/changeLog'
import type { ProcedureType } from 'types/procedures'
import type { PaginatedRequest } from 'types/types'

import { ChangeLogFilterFields } from './utils'

import api from 'api/api'
import {
  ControlledDatePicker,
  ControlledSelect,
  ControlledSelectAutocomplete,
} from 'components/Form'
import { filtersForm } from 'components/Schedule/filters.styles'
import { useRequest } from 'hooks/useRequest'
import i18next from 'i18n'
import {
  ActionSourceEnum,
  ActionTypeEnum,
  ModifiedFieldEnum,
} from 'utils/enums'
import { enum2SelectOptions } from 'utils/forms'
import {
  actionSourceLabels,
  actionTypeLabels,
  modifiedFieldLabels,
} from 'utils/labels'
import { DATE_FORMAT } from 'utils/time'

type TreatmentChangeLogFilters = {
  getTreatmentChangeLogFilters: (data: ChangeLogParamsType) => void
}

const withEmptyOption = (options: { value: string; label: string }[]) => [
  { value: '', label: i18next.t('All') },
  ...options,
]

const TreatmentChangeLogFilters = ({
  getTreatmentChangeLogFilters,
}: TreatmentChangeLogFilters) => {
  const { t } = useTranslation()

  const { data: proceduresData, doRequest: getProcedures } = useRequest<
    PaginatedRequest<ProcedureType>
  >(api.getProcedures)

  const proceduresOptions = useMemo(
    () =>
      proceduresData?.data?.map(procedure => ({
        value: procedure.id,
        label: procedure.name,
      })) || [],
    [proceduresData?.data],
  )

  const form = useForm({
    defaultValues: {
      [ChangeLogFilterFields.PROCEDURE_ID]: '',
      [ChangeLogFilterFields.MODIFIED_AT_FROM]: '',
      [ChangeLogFilterFields.MODIFIED_AT_TO]: '',
      [ChangeLogFilterFields.ACTION_SOURCE]: undefined,
      [ChangeLogFilterFields.ACTION_TYPE]: undefined,
      [ChangeLogFilterFields.MODIFIED_FIELD]: undefined,
    },
  })
  const onFilterSubmit = (data: ChangeLogParamsType) => {
    const formattedData = {
      ...data,
      [ChangeLogFilterFields.MODIFIED_AT_FROM]: data.modifiedAtFrom
        ? `${dayjs(data.modifiedAtFrom).format(DATE_FORMAT)}T00:00:00`
        : '',
      [ChangeLogFilterFields.MODIFIED_AT_TO]: data.modifiedAtTo
        ? `${dayjs(data.modifiedAtTo).format(DATE_FORMAT)}T23:59:59`
        : '',
    }

    getTreatmentChangeLogFilters(formattedData)
  }
  useEffect(() => {
    getProcedures()
  }, [getProcedures])

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onFilterSubmit)}
        className={filtersForm()}
      >
        <Flex
          gap="sm"
          align="end"
          justify="spaceBetween"
          css={{ width: '100%' }}
        >
          <Flex gap="xxsm">
            <ControlledSelectAutocomplete
              name={`${ChangeLogFilterFields.PROCEDURE_ID}`}
              options={withEmptyOption(proceduresOptions)}
              label={t('Procedures')}
              placeholder={t('Select procedure')}
            />

            <ControlledSelect
              name={ChangeLogFilterFields.ACTION_SOURCE}
              options={withEmptyOption(
                enum2SelectOptions(ActionSourceEnum, actionSourceLabels),
              )}
              label={t('Action Source')}
              placeholder={t('All')}
            />

            <ControlledSelect
              name={ChangeLogFilterFields.ACTION_TYPE}
              options={withEmptyOption(
                enum2SelectOptions(ActionTypeEnum, actionTypeLabels),
              )}
              label={t('Action Type')}
              placeholder={t('All')}
            />

            <ControlledSelect
              name={ChangeLogFilterFields.MODIFIED_FIELD}
              options={withEmptyOption(
                enum2SelectOptions(ModifiedFieldEnum, modifiedFieldLabels),
              )}
              label={t('Modified Field')}
              placeholder={t('All')}
            />

            <ControlledDatePicker
              name={ChangeLogFilterFields.MODIFIED_AT_FROM}
              label={t('Modified From')}
            />

            <ControlledDatePicker
              name={ChangeLogFilterFields.MODIFIED_AT_TO}
              label={t('Modified To')}
            />
          </Flex>
          <Flex gap="xxxsm">
            <Button
              type="submit"
              variant="success"
              css={{ height: 32 }}
              disabled={!form.formState.isDirty}
            >
              {t('Filter')}
            </Button>
            <Button
              variant="neutral"
              css={{ height: 32 }}
              onClick={() => {
                form.reset()
                getTreatmentChangeLogFilters({})
              }}
            >
              {t('Clear')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  )
}

export default TreatmentChangeLogFilters
